import { useEffect, useState } from 'react';
import { ArrowUp, Check, ShieldCheck } from 'lucide-react';
import type {
  ApprovalChatInteraction,
  ChatInteractionSubmission,
} from '@/services/api';
import { cn } from '@/lib/utils';

interface ApprovalInteractionProps {
  interaction: ApprovalChatInteraction;
  disabled?: boolean;
  onSubmit: (
    interaction: ApprovalChatInteraction,
    submission: ChatInteractionSubmission
  ) => Promise<boolean>;
}

export function ApprovalInteraction({
  interaction,
  disabled = false,
  onSubmit,
}: ApprovalInteractionProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (interaction.status === 'submitted') {
      setSelectedOptionId(interaction.response?.optionId ?? null);
      setSubmitting(false);
    }
  }, [interaction.response?.optionId, interaction.status]);

  if (interaction.status === 'submitted' && interaction.response) {
    return (
      <div className="mt-3 w-[min(28rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50/70 shadow-sm dark:border-emerald-900/70 dark:bg-emerald-950/25">
        <div className="flex items-start gap-3 px-4 py-3.5">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white dark:bg-emerald-500">
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Answer submitted
            </p>
            <p className="mt-1 text-sm font-medium text-surface-900 dark:text-surface-100">
              {interaction.response.optionLabel}
            </p>
            {interaction.submittedBy && (
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                Submitted by {interaction.submittedBy.username}
              </p>
            )}
            {!interaction.resumeCompleted && (
              <p className="mt-1 text-xs text-primary-600 dark:text-primary-300">
                The agent response is continuing.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = Boolean(selectedOptionId) && !disabled && !submitting;

  const handleSubmit = async () => {
    if (!selectedOptionId || !canSubmit) return;
    setSubmitting(true);
    const accepted = await onSubmit(interaction, { optionId: selectedOptionId });
    if (!accepted) setSubmitting(false);
  };

  return (
    <div className="mt-3 w-[min(28rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-surface-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900">
      <div className="border-b border-surface-100 px-4 py-3.5 dark:border-surface-800">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
          <ShieldCheck className="h-4 w-4" />
          Approval needed
        </div>
        <p className="mt-2 text-sm font-medium leading-5 text-surface-900 dark:text-surface-100">
          {interaction.request.question}
        </p>
      </div>

      <div className="space-y-1.5 p-3">
        {interaction.request.options.map((option) => {
          const selected = option.id === selectedOptionId;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              disabled={disabled || submitting}
              onClick={() => setSelectedOptionId(option.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
                selected
                  ? 'bg-primary-50 text-primary-950 dark:bg-primary-950/45 dark:text-primary-100'
                  : 'text-surface-700 hover:bg-surface-50 dark:text-surface-200 dark:hover:bg-surface-800/80',
                (disabled || submitting) && 'cursor-not-allowed opacity-65'
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                  selected
                    ? 'border-primary-600 bg-primary-600 dark:border-primary-400 dark:bg-primary-400'
                    : 'border-surface-300 bg-white dark:border-surface-600 dark:bg-surface-900'
                )}
              >
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full bg-white transition-transform dark:bg-surface-950',
                    selected ? 'scale-100' : 'scale-0'
                  )}
                />
              </span>
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t border-surface-100 px-3 py-2.5 dark:border-surface-800">
        <span className="px-1 text-xs text-surface-500 dark:text-surface-400">
          Select one option
        </span>
        <button
          type="button"
          aria-label="Submit approval response"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg transition-all',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
            canSubmit
              ? 'bg-surface-900 text-white hover:bg-surface-700 active:scale-95 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200'
              : 'cursor-not-allowed bg-surface-100 text-surface-400 dark:bg-surface-800 dark:text-surface-600'
          )}
        >
          <ArrowUp className={cn('h-4 w-4', submitting && 'animate-pulse')} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
